
import { toast } from 'sonner';
import { adminLog } from '@/lib/utils/admin-logger';
import { getAggregatedStats } from '@/lib/analytics';
import { AuthFilter } from '@/components/analytics/AuthFilterButtons';
import { ReferrerStat } from '@/lib/analytics/types';

// Helper function for fetching individual stats
export const fetchStatWithErrorHandling = async (
  fetchFn: () => Promise<any>,
  setData: (data: any) => void,
  setLoading: (loading: boolean) => void,
  setError: (error: Error | null) => void,
  metricName: string
) => {
  try {
    setLoading(true);
    setError(null);
    const data = await fetchFn();
    
    // Check if data is valid (array or object) before setting
    if (Array.isArray(data)) {
      // Reduced logging - only log the count of items
      adminLog(`Successfully fetched ${metricName}: ${data.length} records`);
      setData(data);
      return data;
    } else if (data && typeof data === 'object') {
      // Handle object responses (like session metrics)
      adminLog(`Successfully fetched ${metricName}`);
      setData(data);
      return data;
    } else {
      // If data is neither array nor object, log the issue
      adminLog(`Expected array or object for ${metricName}, but got: ${typeof data}`);
      setData(null);
      return null;
    }
  } catch (error) {
    console.error(`Error fetching ${metricName}:`, error);
    setError(error instanceof Error ? error : new Error(`Failed to load ${metricName}`));
    toast.error(`Failed to load ${metricName}`);
    // Set null on error
    setData(null);
    return null;
  } finally {
    setLoading(false);
  }
};

// Alternative function name for consistency
export const fetchStatsWithErrorHandling = fetchStatWithErrorHandling;

/**
 * Groups similar actions by removing specific identifiers
 * 
 * @param actions Array of action stats with action_type and count
 * @returns Grouped actions array
 */
export const groupSimilarActions = (actions: any[]): any[] => {
  const actionGroups: Record<string, number> = {};
  
  actions.forEach(item => {
    // Extract the generic action type by removing paper specific parts
    const actionType = item.action_type || '';
    const genericAction = actionType.includes(':') 
      ? actionType.split(':')[0].trim() 
      : actionType;
    
    if (genericAction) {
      actionGroups[genericAction] = (actionGroups[genericAction] || 0) + item.count;
    }
  });
  
  // Convert to array format needed for the chart
  return Object.entries(actionGroups).map(([action_type, count]) => ({
    action_type,
    count,
  }));
};

/**
 * Creates enhanced action labels with context
 * 
 * @param actions Array of action stats with action_type and count
 * @returns Actions with enhanced display names
 */
export const createEnhancedActionLabels = (actions: any[]): any[] => {
  // Map of action types to their display names
  const actionDisplayNames: Record<string, string> = {
    'pageview': 'Page View',
    'interaction': 'User Interaction',
    'click': 'Click',
    'submit': 'Form Submit',
    'download': 'Download',
    // Add more mappings as needed
  };
  
  const actionGroups: Record<string, number> = {};
  const pagePrefixes: Record<string, Set<string>> = {};
  
  // First pass: collect data
  actions.forEach(item => {
    const actionType = item.action_type || '';
    let genericAction = actionType;
    let pagePath = '';
    
    if (actionType.includes(':')) {
      const [action, context] = actionType.split(':', 2);
      genericAction = action.trim();
      pagePath = context.split('/')[0]?.trim() || '';
      
      if (pagePath) {
        if (!pagePrefixes[genericAction]) {
          pagePrefixes[genericAction] = new Set();
        }
        pagePrefixes[genericAction].add(pagePath);
      }
    }
    
    if (genericAction) {
      actionGroups[genericAction] = (actionGroups[genericAction] || 0) + item.count;
    }
  });
  
  // Second pass: create enhanced labels
  return Object.entries(actionGroups).map(([action, count]) => {
    let enhancedLabel = actionDisplayNames[action] || action;
    
    if (pagePrefixes[action] && pagePrefixes[action].size > 0) {
      const pages = Array.from(pagePrefixes[action]);
      
      if (pages.length === 1) {
        enhancedLabel += ` on ${pages[0]}`;
      } else if (pages.length <= 3) {
        enhancedLabel += ` on ${pages.slice(0, -1).join(', ')} & ${pages[pages.length - 1]}`;
      } else {
        enhancedLabel += ` on ${pages.length} pages`;
      }
    }
    
    return {
      action_type: action,
      display_name: enhancedLabel,
      count,
      contexts: Array.from(pagePrefixes[action] || []),
    };
  });
};

export const fetchReferrerStats = async (
  startDate: Date,
  endDate: Date,
  authFilter: AuthFilter
): Promise<ReferrerStat[]> => {
  return getAggregatedStats(startDate, endDate, 'referrer', authFilter) as Promise<ReferrerStat[]>;
};
